/* eslint-disable no-nested-ternary */
/* eslint-disable */
import {
	useCallback, useEffect, useRef, useState
} from 'react';
import SelectPicker from 'rsuite/SelectPicker';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import styles from './MultiCascaderTree.module.scss';

// Height of the sticky colour-header strip (including its bottom padding), and
// of one row. HEADER_HEIGHT is where every open row pins; ROW_HEIGHT only
// nudges the search scroll-to so the picked row lands on centre.
const HEADER_HEIGHT = 49;
const ROW_HEIGHT = 41;

const negativeSvg = (
	<svg
		id='i-close'
		viewBox='0 0 32 32'
		width='16'
		height='16'
		fill='none'
		stroke='currentcolor'
		strokeLinecap='round'
		strokeLinejoin='round'
		strokeWidth='10.9375%'
	>
		<path d='M2 30 L30 2 M30 30 L2 2' />
	</svg>
);
const positiveSvg = (
	<svg
		id='i-checkmark'
		viewBox='0 0 32 32'
		width='16'
		height='16'
		fill='none'
		stroke='currentcolor'
		strokeLinecap='round'
		strokeLinejoin='round'
		strokeWidth='10.9375%'
	>
		<path d='M2 20 L12 28 30 4' />
	</svg>
);

function TableDrop({
	data, width, handleChange, headerWidth, parentSelection, expandedIds, setExpandedIds,
	level = 0, highlightId,
}) {
	const toggleExpand = (id, type) => {
		const localExpanded = expandedIds;
		if (localExpanded[type] === id) {
			localExpanded[type] = null;
		} else {
			localExpanded[type] = id;
		}
		setExpandedIds({
			...localExpanded,
		});
	};

	return (
		<div>
			{data?.map((element) => {
				let backgroundColor = '';
				const parentStatus = element.select === 0 ? parentSelection : element.select;
				const currentStatusPos = (element.select === 0 && parentSelection === 1) || element.select === 1;
				const currentStatusNeg = (element.select === 0 && parentSelection === -1) || element.select === -1;
				const nodeId = element.id + '-' + element.type;
				const isExpanded = expandedIds?.[element?.type] === nodeId;
				if (isExpanded) {
					backgroundColor = 'white';
				}
				return (
					<div
						key={nodeId}
						style={{
							backgroundColor: element.color || 'transparent',
							width: width + 'px',
							borderBottom: '1px solid #f1f5f9',
							borderRight: '1px solid #f1f5f9',
							// No `overflow: hidden` here on purpose: an overflow-clipped
							// ancestor cancels position:sticky on the row label inside.
							// The outer scroll box still clips horizontally.
							transition: 'all 0.2s ease',
						}}
					>
						<div
							data-node-id={nodeId}
							style={{
								backgroundColor: backgroundColor || 'transparent',
								padding: '8px 12px',
								width: headerWidth + 'px',
								display: 'flex',
								justifyContent: 'space-between',
								alignItems: 'center',
								fontWeight: '500',
								overflow: 'hidden',
								cursor: 'pointer',
								// Only an OPEN row pins: it is the one whose children are
								// scrolling past, and it already has an opaque white
								// background so the rows sliding under it stay hidden.
								// Every open row pins at the SAME height, right under the
								// colour strip. Each level lives in its own column, so
								// the four of them squash onto one line and the whole
								// trail (All Regions | North | Delhi | New Delhi) stays
								// readable while costing a single row of space. Depth
								// order takes care of itself: a child is nested inside
								// its parent, so it paints on top.
								...(isExpanded ? {
									position: 'sticky',
									top: HEADER_HEIGHT + 'px',
									zIndex: 40 + level,
									boxShadow: '0 1px 0 #e2e8f0',
								} : {}),
								...(highlightId === nodeId ? {
									outline: '2px solid #4a70fb',
									outlineOffset: '-2px',
								} : {}),
							}}
						>
							<span
								className={styles.checkbox + ' ' +
									(element.select === 1
										? styles.positive
										: element.select === -1
											? styles.negative
											: ''
									)}
								onClick={() => {
									handleChange(element.id + '-' + element.type, parentSelection, element);
								}}
							>
								{element.select === 1
									? positiveSvg
									: element.select === -1
										? negativeSvg
										: ''}
							</span>
							<div
								style={{
									width: '100%',
									display: 'flex',
									justifyContent: 'space-between',
								}}
								onClick={() => {
									toggleExpand(element.id + '-' + element.type, element.type);
								}}
							>
								<span
									style={{
										fontSize: '15px',
										userSelect: 'none',
										display: 'flex',
										alignItems: 'center',
									}}
								>
									{currentStatusPos && (
										<div style={{
											color: 'white',
											backgroundColor: 'green',
											marginRight: '3px',
											justifyContent: 'center',
											alignItems: 'center',
											display: 'flex',
											minWidth: '10px',
											width: '10px',
											height: '10px',
											borderRadius: '50%',
										}}
										>
											{' '}
										</div>
									)}
									{currentStatusNeg && (
										<div style={{
											color: 'white',
											backgroundColor: 'red',
											marginRight: '3px',
											justifyContent: 'center',
											alignItems: 'center',
											display: 'flex',
											minWidth: '10px',
											width: '10px',
											height: '10px',
											borderRadius: '50%',
										}}
										>
											{' '}
										</div>
									)}
									{element.name}
								</span>

								<span>
									{element.children?.length > 0 && (
										<KeyboardArrowRightIcon />
									)}
								</span>
							</div>
						</div>
						{element?.children?.length !== 0 &&
							expandedIds[element?.type] === element?.id + '-' + element?.type ? (
							<div
								style={{
									transform: 'translateX(' + headerWidth + 'px)',
								}}
							>
								<TableDrop
									data={element.children}
									width={width - headerWidth}
									headerWidth={headerWidth}
									handleChange={handleChange}
									parentSelection={parentStatus}
									expandedIds={expandedIds}
									setExpandedIds={setExpandedIds}
									level={level + 1}
									highlightId={highlightId}
								/>
							</div>
						) : (
							''
						)}
					</div>
				);
			})}
		</div>
	);
}

function MultiCascaderTree({
	inputState = [],
	setInputState,
	headers = [],
	widthPercentage = 75,
	disabled = false,
	externalExpandedIds,
	trackSelectionChange = null,
}) {
	const propWidth = (window.innerWidth || window.screen.width) * (widthPercentage / 100);
	const setRemoveToChilds = (nodes, status) => {
		return nodes?.map((ele) => {
			const node = ele;
			if (node.select === 1) {
				return node;
			}
			node.notRemove = status;
			if (status === 1) {
				node.select = 0;
			}
			setRemoveToChilds(node.children, status);
			return node;
		});
	};
	const findNodeById = (nodes, id, parentStatus) => {
		return nodes.map((ele) => {
			const node = ele;
			if (node.id + '-' + node.type === id) {
				if (node.select === 1) {
					if (node.type === 'root') {
						node.select = -1;
					} else if (node.notRemove || parentStatus === -1) {
						node.select = 0;
					} else {
						node.select = -1;
					}
				} else if (node.type === 'root') {
					node.select = 1;
				} else node.select += 1;
				if (node.select === -1) {
					setRemoveToChilds(node?.children, 1);
				} else if (node.select === 0) {
					node.notRemove = parentStatus === -1 ? 1 : 0;
					setRemoveToChilds(node?.children, node?.notRemove);
				} else {
					setRemoveToChilds(node?.children, 0);
				}
			} else if (node.children?.length) {
				findNodeById(node.children, id, parentStatus);
			}
			return node;
		});
	};

	const handleChange = (id, parentStatus, element) => {
		const tempNodes = findNodeById(inputState, id, parentStatus);
		setInputState([...tempNodes]);
		if (trackSelectionChange) {
			trackSelectionChange(id, element);
		}
	};

	const headerLength = headers?.length ?? 5;
	const headerWidth = parseInt(propWidth / headerLength, 10);

	const [searchData, setSearchData] = useState({});

	const [expandedIds, setExpandedIds] = useState({});
	// The row picked from the search box: scrolled into view and ringed for a
	// moment once the tree has re-rendered with it expanded.
	const [highlightId, setHighlightId] = useState(null);
	const scrollRef = useRef(null);
	const highlightTimer = useRef(null);

	useEffect(() => {
		return () => {
			clearTimeout(highlightTimer.current);
		};
	}, []);

	useEffect(() => {
		if (externalExpandedIds) {
			setExpandedIds(externalExpandedIds);
		}
	}, [externalExpandedIds]);

	const mapSearchedData = (
		regionArray,
		possibleSearch,
		searchList,
		parentIdMapping
	) => {
		regionArray?.forEach((region) => {
			const localPs = possibleSearch + region?.name;
			const localParentIdMapping = {
				...parentIdMapping,
			}; // Create a new object
			localParentIdMapping[region?.type] =
				region?.id + '-' + region?.type;
			const localSearchList = searchList;
			localSearchList[localPs] = localParentIdMapping;
			setSearchData(localSearchList);
			mapSearchedData(
				region.children,
				localPs + '/',
				localSearchList,
				localParentIdMapping
			);
		});
	};

	useEffect(() => {
		mapSearchedData(inputState, '', {}, {});
	}, [inputState]);

	const SEARCHDATA = useCallback(() => {
		const dataPaths = Object.entries(searchData).map(([key, value]) => {
			return {
				label: key, value,
			};
		});
		return dataPaths;
	}, [searchData]);

	const handleSelect = (item) => {
		// Copied: toggleExpand mutates the expandedIds object in place, and the
		// search index holds these same objects — sharing one would corrupt it.
		setExpandedIds({ ...item });
		// The deepest id in the mapping is the row the user actually picked —
		// the object is built root-first while walking down the tree.
		const targetId = Object.values(item || {}).pop();
		if (!targetId) {
			return;
		}
		setHighlightId(targetId);
		clearTimeout(highlightTimer.current);
		highlightTimer.current = setTimeout(() => {
			setHighlightId(null);
		}, 2500);
		// Two frames: one for React to commit the newly expanded branches, one
		// for the browser to lay them out before we measure where the row is.
		requestAnimationFrame(() => {
			requestAnimationFrame(() => {
				const container = scrollRef.current;
				const node = container?.querySelector(
					'[data-node-id="' + targetId + '"]'
				);
				if (!container || !node) {
					return;
				}
				const offset = node.getBoundingClientRect().top -
					container.getBoundingClientRect().top;
				container.scrollTo({
					top: (container.scrollTop + offset) -
						((container.clientHeight / 2) - ROW_HEIGHT),
					behavior: 'smooth',
				});
			});
		});
	};

	return (
		<div
			style={{
				width: propWidth + 'px',
			}}
		>
			{disabled && (
				<div className={styles.disabled} />
			)}
			<div
				style={{
					margin: '5px',
					maxWidth: '400px',
					width: '400px',
				}}
			>
				<SelectPicker
					style={{
						minWidth: 400,
					}}
					data={SEARCHDATA()}
					menuStyle={{
						zIndex: 2000,
						maxHeight: '50%',
					}}
					cleanable={false}
					onSelect={(val) => {
						handleSelect(val);
					}}
				/>
			</div>
			<div
				ref={scrollRef}
				style={{
					width: propWidth + 'px',
					overflowY: 'auto',
					overflowX: 'hidden',
					height: 'calc(100% - 48px)',
					// Fallback cap: several hosts (AuditStep2, the drill-down modals)
					// give this component no definite height, so calc() collapses to
					// auto and THEIR wrapper does the scrolling — which leaves the
					// sticky header with nothing to stick to. The cap guarantees this
					// box is the scroller.
					maxHeight: '70vh',
					position: 'relative',
				}}
			>
			<div
				style={{
					display: 'flex',
					position: 'sticky',
					top: 0,
					zIndex: 60,
					backgroundColor: '#fff',
					// Breathing room under the colour strip. It is padding, not
					// margin, so it travels WITH the sticky header and keeps
					// covering the rows that scroll beneath it.
					paddingBottom: '8px',
				}}
			>
				{headers &&
					headers.length > 0 &&
					headers.map((region) => {
						return (
							<div
								key={region.id}
								style={{
									flex: '1',
									backgroundColor: region.color,
									padding: '12px 10px',
									textAlign: 'center',
									width: headerWidth + 'px',
									borderRight: '2px solid #fff',
								}}
							>
								<span
									style={{
										margin: '0',
										color: region.textColor || '#fff',
										fontSize: '13px',
										fontWeight: '700',
										textTransform: 'uppercase',
										letterSpacing: '0.04em',
										whiteSpace: 'nowrap',
									}}
								>
									{region.label}
								</span>
							</div>
						);
					})}
			</div>
				<TableDrop
					data={inputState}
					width={propWidth}
					headerWidth={headerWidth}
					handleChange={handleChange}
					parentSelection={-1}
					expandedIds={expandedIds}
					setExpandedIds={setExpandedIds}
					highlightId={highlightId}
				/>
			</div>
		</div>
	);
}

export default MultiCascaderTree;
